/**
 * Downscale and transcode an image to JPEG on a canvas, before upload.
 *
 * This is not an optimisation, it is a requirement of the server contract:
 * PAPERCLIP_PHOTOGRAPHIC_CONTENT_TYPES is JPEG/WebP only — no PNG and no HEIC — while
 * an iPhone photo is HEIC natively and a screenshot is a PNG. Transcoding here means an
 * ordinary phone photo arrives as something the server will accept.
 *
 * It also solves the thing that actually breaks in the field: a 12MP camera photo is
 * ~5MB, and pushing that over mobile data (times several) is where an upload dies.
 * Redrawing through a canvas normalises EXIF orientation into the pixels at the same
 * time, so a portrait photo isn't published on its side.
 */

// 2048px on the long edge at 0.85 quality: comfortably above the largest style the
// server renders (PAPERCLIP_STANDARD_STYLES tops out well below this), and it takes a
// typical 12MP phone photo from ~5MB to a few hundred KB.
export const DEFAULT_MAX_DIMENSION = 2048;
export const DEFAULT_JPEG_QUALITY = 0.85;
export const JPEG_CONTENT_TYPE = 'image/jpeg';

/**
 * @param {File} file - The file the person picked.
 * @param {Object} [options]
 * @param {number} [options.maxDimension=2048] - Cap on the longest edge, in pixels.
 * @param {number} [options.quality=0.85] - JPEG quality, 0-1.
 * @returns {Promise<File>} A JPEG File. Rejects if the image cannot be decoded at all
 *   (a corrupt file, or a format this browser can't read) — the caller shows that as a
 *   per-file error rather than posting something the server would reject anyway.
 */
export async function transcodeImageToJpeg(file, options = {}) {
    const maxDimension = options.maxDimension || DEFAULT_MAX_DIMENSION;
    const quality = options.quality || DEFAULT_JPEG_QUALITY;

    const image = await decodeImage(file);
    const {width, height} = scaledDimensions(image.width, image.height, maxDimension);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext('2d');
    context.drawImage(image, 0, 0, width, height);

    if (image.close) image.close(); // Release the ImageBitmap's memory — phones are tight.

    const blob = await canvasToJpegBlob(canvas, quality);
    return new File([blob], jpegFilename(file.name), {type: JPEG_CONTENT_TYPE});
}

/**
 * The scaled size that fits inside maxDimension, preserving the aspect ratio. An image
 * already inside the cap is left alone (never upscaled).
 */
export function scaledDimensions(width, height, maxDimension) {
    const longestEdge = Math.max(width, height);
    if (!longestEdge || longestEdge <= maxDimension) return {width, height};

    const scale = maxDimension / longestEdge;
    return {
        width: Math.round(width * scale),
        height: Math.round(height * scale)
    };
}

function jpegFilename(name) {
    const base = (name || 'photo').replace(/\.[^.]+$/, '');
    return `${base}.jpg`;
}

// `createImageBitmap` with `imageOrientation: 'from-image'` bakes the EXIF rotation into
// the bitmap. Where it isn't available (or refuses the format) we fall back to an <img>,
// which browsers also orient from EXIF for image documents.
function decodeImage(file) {
    if (typeof createImageBitmap === 'function') {
        return createImageBitmap(file, {imageOrientation: 'from-image'})
            .catch(() => decodeImageElement(file));
    }
    return decodeImageElement(file);
}

function decodeImageElement(file) {
    return new Promise((resolve, reject) => {
        const objectUrl = URL.createObjectURL(file);
        const image = new Image();

        image.onload = () => {
            URL.revokeObjectURL(objectUrl);
            resolve(image);
        };
        image.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            reject(new Error(`"${file.name}" could not be read as an image.`));
        };

        image.src = objectUrl;
    });
}

function canvasToJpegBlob(canvas, quality) {
    return new Promise((resolve, reject) => {
        if (!canvas.toBlob) {
            reject(new Error('This browser cannot prepare the photo for upload.'));
            return;
        }
        canvas.toBlob(
            (blob) => blob ? resolve(blob) : reject(new Error('The photo could not be prepared for upload.')),
            JPEG_CONTENT_TYPE,
            quality
        );
    });
}
